import { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../utils/authHeader";

export interface UseAutopilotEnabledReturn {
    // null while loading or on fetch failure - callers treat null as
    // "unknown" and keep today's behavior (the API 409 stays the authority).
    autopilotEnabled: boolean | null;
    // true until the request settles. The flag can only be fetched once the
    // consumer mounts - which is itself gated on the listings request - so
    // without this callers render the manual action for a full round trip
    // and then pull it away. A failed fetch settles to (false, null), i.e.
    // exactly today's behavior.
    loading: boolean;
}

// undefined = never fetched, null = fetched but unknown (request failed).
type AutopilotFlag = boolean | null;

// One flag per user, so one copy per app - not one per component. The MLS
// setup wizard enables Autopilot while the listings page sits mounted behind
// it, and a per-mount copy stays false there until a full reload, re-offering
// the manual Create Campaign the wizard just took away.
let sharedFlag: AutopilotFlag | undefined;
let inflight: Promise<void> | null = null;
// Bumped by every publish so a GET issued before the change cannot land after
// it and overwrite the newer answer.
let generation = 0;
const subscribers = new Set<() => void>();

const notify = (): void => {
    subscribers.forEach((subscriber) => subscriber());
};

// Call after a successful autopilot PATCH: consumers mounted before the change
// flip immediately instead of waiting to be remounted.
export const publishAutopilotEnabled = (enabled: boolean): void => {
    sharedFlag = enabled;
    generation += 1;
    notify();
};

// Deduped: concurrent mounts share one request instead of firing one each.
const fetchFlag = (): Promise<void> => {
    if (inflight) return inflight;
    const gen = generation;
    const request = (async (): Promise<void> => {
        try {
            const headers = await getAuthHeader();
            const { data } = await axios.get<{
                settings?: { enabled?: boolean };
            }>(`${process.env.REACT_APP_BACKEND_URL}/settings/autopilot`, {
                headers,
            });
            if (gen === generation) {
                sharedFlag = data?.settings?.enabled === true;
            }
        } catch {
            // A failed revalidate keeps the value we already have - dropping to
            // null reads as "unknown" and re-shows the manual action.
            if (sharedFlag === undefined) sharedFlag = null;
        } finally {
            inflight = null;
            notify();
        }
    })();
    inflight = request;
    return request;
};

// MB-1436: whether the user's Autopilot is on - used to hide the manual
// Create Campaign action for listings Autopilot manages itself.
export const useAutopilotEnabled = (): UseAutopilotEnabledReturn => {
    const [flag, setFlag] = useState<AutopilotFlag | undefined>(sharedFlag);
    const [loading, setLoading] = useState(sharedFlag === undefined);

    useEffect(() => {
        let cancelled = false;
        const sync = (): void => {
            if (cancelled) return;
            setFlag(sharedFlag);
            setLoading(sharedFlag === undefined && inflight !== null);
        };
        subscribers.add(sync);
        // Still revalidates per mount, as before: a known value is served
        // straight away and corrected in the background if it moved.
        void fetchFlag();
        sync();
        return () => {
            cancelled = true;
            subscribers.delete(sync);
        };
    }, []);

    return { autopilotEnabled: flag ?? null, loading };
};
